// Simulated 30-day operational dataset — internally reconciled.
// Base values are PRE-live-demo; the live demo applies deltas on top.

export const ROI_BASE = {
  summary: {
    detected: 196, eligible: 146, automated: 115, human: 31, governancePrevented: 14,
    assistedProgressions: 32, assistedFtdValue: 40000, assistedRetentionValue: 96400, avgInterventionSec: 8,
  },
  primary: {
    assistedRevenue: 40000,
    recoveredOpportunities: 16,
    hoursRedirected: 31.4,
    avgInterventionSec: 8,
    manualBaselineMin: 18,
  },
  reconciliation: [
    { key: "assisted", label: "Assisted Value Recovered", value: 40000, deltaKey: "assistedFtdValue", tone: "emerald" },
    { key: "progress", label: "Still in Progress", value: 81000, tone: "sky" },
    { key: "organic", label: "Organic Outcomes", value: 16000, deltaKey: "organicValue", tone: "amber" },
    { key: "unrecovered", label: "Unrecovered / Closed", value: 89000, tone: "zinc" },
  ],
  valueAtRiskTotal: 226000,
  leakageAreas: [
    { area: "FTD / Funding", opportunities: 24, value: 61500 },
    { area: "KYC Progression", opportunities: 16, value: 38000 },
    { area: "Retention", opportunities: 11, value: 74000 },
    { area: "Human Escalation", opportunities: 7, value: 52500 },
  ],
  leakageStages: [
    {
      stage: "Registration → Funding", value: 61500,
      clients: [
        { name: "Marco De Luca", ref: "ONET-1042", issue: "FTD abandonment — calls unanswered", value: 9500 },
        { name: "Carlos Mendes", ref: "ONET-1154", issue: "Fee comparison — no reply sent", value: 7200 },
        { name: "Hana Kobayashi", ref: "ONET-1135", issue: "Registered — no engagement", value: 6000 },
      ],
    },
    {
      stage: "Funding → KYC", value: 38000,
      clients: [
        { name: "Aisha Rahman", ref: "ONET-1087", issue: "Proof of address rejected (R-112)", value: 14200 },
        { name: "Fatima Al-Sayed", ref: "ONET-1066", issue: "Mobile capture abandoned twice", value: 8900 },
      ],
    },
    {
      stage: "KYC → Activation", value: 25700,
      clients: [
        { name: "Jonas Weber", ref: "ONET-1023", issue: "€5,000 deposit failed — 3DS code 05", value: 11800 },
      ],
    },
    {
      stage: "Active → Retention Risk", value: 74000,
      clients: [
        { name: "Elena Petrova", ref: "ONET-0961", issue: "9 days no login — volume -62%", value: 21500 },
        { name: "Ingrid Larsen", ref: "ONET-0876", issue: "Dormant 34 days — €9,800 equity", value: 9800 },
      ],
    },
    {
      stage: "Human Escalation Pending", value: 26800,
      clients: [
        { name: "David Okafor", ref: "ONET-1110", issue: "Withdrawal support event — senior review", value: 26800 },
      ],
    },
  ],
  funnels: {
    ftd: {
      title: "FTD Recovery Funnel",
      steps: [
        { label: "FTD opportunities detected", value: 120 },
        { label: "Eligible for intervention", value: 94 },
        { label: "Automated actions", value: 72 },
        { label: "Human escalations", value: 22 },
        { label: "Clients retried funding", value: 29 },
        { label: "Assisted FTDs", value: 14, deltaKey: "assistedFtdCount" },
        { label: "Assisted FTD value", value: 40000, money: true, deltaKey: "assistedFtdValue" },
      ],
      note: "Detected → Eligible → Actioned → Responded → Converted → Attributed. Not every detected opportunity converts.",
    },
    kyc: {
      title: "KYC Progression Funnel",
      steps: [
        { label: "KYC journeys stalled", value: 48 },
        { label: "Eligible for reminder", value: 31 },
        { label: "Automated reminders", value: 27 },
        { label: "Human escalations", value: 4 },
        { label: "Clients returned to KYC", value: 16 },
        { label: "Document progression", value: 12 },
      ],
      note: "Verification progression — activation opportunity preserved, not revenue recovery.",
    },
    retention: {
      title: "Retention Intelligence",
      steps: [
        { label: "Clients flagged at risk", value: 28 },
        { label: "Eligible for re-engagement", value: 21 },
        { label: "Automated re-engagement", value: 16 },
        { label: "Human escalations", value: 5 },
        { label: "Clients re-engaged", value: 6, deltaKey: "reengaged" },
        { label: "Assisted account value", value: 96400, money: true },
      ],
      note: "Assisted Account Value — not revenue recovered; no deposit events are implied.",
    },
  },
  responseTimes: {
    onetech: [
      { label: "FTD event", sec: 6 },
      { label: "KYC stall", sec: 11 },
      { label: "Retention trigger", sec: 14 },
      { label: "Human escalation creation", sec: 4 },
    ],
    manual: [
      { label: "FTD follow-up", label2: "18 min" },
      { label: "KYC queue", label2: "42 min" },
      { label: "Retention review", label2: "4.3 hrs" },
    ],
  },
  performance: [
    { workflow: "Funding Recovery", actions: 72, progressions: 14, deltaActions: 0, deltaProgressions: "assistedFtdCount" },
    { workflow: "KYC Reminder", actions: 27, progressions: 12 },
    { workflow: "Retention Re-engagement", actions: 16, progressions: 6, deltaProgressions: "reengaged" },
  ],
  distribution: { automated: 115, human: 31, suppressed: 14, deltaHuman: "escalations", deltaSuppressed: "governance" },
  humanImpact: {
    open: 7, value: 184000, slaWithin: 92, breaches: 2, resolved: 24, humanAssistedOutcomes: 6,
  },
  attribution: [
    { key: "assisted", label: "Assisted by O.N.E.Tech", value: 40000, deltaKey: "assistedFtdValue", tone: "emerald" },
    { key: "human", label: "Human Assisted", value: 31000, tone: "sky" },
    { key: "organic", label: "Organic", value: 16000, deltaKey: "organicValue", tone: "amber" },
    { key: "excluded", label: "Excluded", value: 8500, tone: "zinc" },
  ],
  attributionClients: [
    {
      name: "Maria Rossi", issue: "FTD abandonment — 3DS abandoned", intervention: "Italian WhatsApp funding recovery",
      outcome: "FTD Confirmed", value: "€2,500", attribution: "Assisted",
      evidence: { action: "14:21:06 — WhatsApp (IT) sent", retry: "14:22:37 — client payment retry", confirm: "14:23:11 — €2,500 FTD confirmed", window: "Valid — outcome within attribution window", prior: "None" },
    },
    {
      name: "Luca Bianchi", issue: "Retention risk — 9 days inactive", intervention: "Italian WhatsApp re-engagement",
      outcome: "Re-engaged — platform login", value: "€18,400 account value", attribution: "Assisted",
      evidence: { action: "07:15:41 — WhatsApp (IT) sent", retry: "11:04:47 — platform login", confirm: "11:26:05 — trading session resumed", window: "Valid", prior: "None" },
    },
    {
      name: "James Carter", issue: "Repeated funding failure (3x)", intervention: "Human payments call after automation limit",
      outcome: "FTD via bank transfer", value: "£8,000", attribution: "Human Assisted",
      evidence: { action: "11:52:12 — CRM task to Payments (SLA 10m)", retry: "12:03:22 — agent call", confirm: "12:09:41 — £8,000 settled", window: "Valid", prior: "O.N.E.Tech routed; agent completed" },
    },
    {
      name: "Sofia Martinez", issue: "KYC stalled — proof of address", intervention: "Spanish KYC reminder email",
      outcome: "KYC Review Pending", value: "Verification progression", attribution: "Assisted",
      evidence: { action: "08:14:54 — Email (ES) sent", retry: "08:16:03 — KYC link clicked", confirm: "08:17:44 — document uploaded", window: "Valid", prior: "None" },
    },
    {
      name: "Thomas Evans", issue: "Registered — observation window", intervention: "None — waiting window active",
      outcome: "Organic FTD", value: "£3,000", attribution: "Organic",
      evidence: { action: "No O.N.E.Tech action", retry: "17:02:41 — deposit initiated independently", confirm: "17:03:15 — £3,000 confirmed", window: "Not applicable", prior: "None — explicitly unattributed" },
    },
    {
      name: "Emma Johnson", issue: "FTD opportunity — opted out", intervention: "None — suppression rule",
      outcome: "No contact", value: "€0 — foregone", attribution: "Excluded",
      evidence: { action: "Suppression active — no action", retry: "—", confirm: "—", window: "Not applicable", prior: "Preference overrides opportunity" },
    },
  ],
  channels: [
    { channel: "WhatsApp", interventions: 18, progressions: 9 },
    { channel: "Email", interventions: 24, progressions: 11 },
    { channel: "SMS", interventions: 11, progressions: 2 },
    { channel: "Human Agent", interventions: 14, progressions: 10 },
  ],
  languages: [
    { language: "English", outcomes: 12 },
    { language: "Italian", outcomes: 9 },
    { language: "Arabic", outcomes: 5 },
    { language: "Spanish", outcomes: 4 },
    { language: "German", outcomes: 2 },
  ],
  governance: {
    prevented: 14,
    sensitiveRouted: 7,
    consentBlocks: 4,
    frequencyBlocks: 3,
    complianceBlocks: 4,
    suppressionBlocks: 3,
    slaEscalations: 2,
  },
  leakageSummary: [
    { label: "Recovered / Progressed", value: 40000, deltaKey: "assistedFtdValue", tone: "emerald" },
    { label: "Still Open", value: 81000, tone: "sky" },
    { label: "Human Review", value: 31500, tone: "violet" },
    { label: "Organic", value: 16000, deltaKey: "organicValue", tone: "amber" },
    { label: "Suppressed / Ineligible", value: 57500, tone: "zinc" },
  ],
  openOpportunity: [
    { area: "Funding", value: 34000 },
    { area: "KYC", value: 17500 },
    { area: "Retention", value: 21000 },
    { area: "Human Escalation", value: 8500 },
  ],
  cost: { platformMonthly: 4000 },
  trends: {
    weeks: ["W1", "W2", "W3", "W4", "W5", "W6", "W7", "W8", "W9", "W10", "W11", "W12"],
    series: [
      { key: "ftd", label: "Assisted FTD Value (€)", data: [2400, 3100, 2800, 3900, 3400, 4200, 3600, 4600, 4100, 3800, 4400, 4000] },
      { key: "kyc", label: "KYC Progressions", data: [0, 1, 1, 2, 1, 1, 2, 1, 1, 1, 1, 1] },
      { key: "retention", label: "Re-engagements", data: [0, 1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 0] },
      { key: "escalations", label: "Human Escalations", data: [2, 3, 2, 4, 3, 2, 3, 3, 2, 4, 2, 1] },
      { key: "suppressed", label: "Suppressed Actions", data: [1, 1, 2, 1, 1, 2, 1, 1, 2, 1, 1, 1] },
    ],
  },
  beforeAfter: {
    before: [
      "Lead enters CRM",
      "Waits in queue",
      "Agent notices later",
      "Manual outreach",
      "No central attribution",
    ],
    after: [
      "Lead event detected",
      "Lifecycle state classified",
      "Policy checked",
      "Action triggered in seconds",
      "Outcome tracked automatically",
      "Revenue attribution recorded",
    ],
  },
};

export const ROI_DELTAS_ZERO = {
  assistedFtdValue: 0, assistedFtdCount: 0, reengaged: 0, escalations: 0, governance: 0, organicValue: 0, organicCount: 0,
};

export const readRoiDelta = () => {
  try {
    return { ...ROI_DELTAS_ZERO, ...JSON.parse(localStorage.getItem("onet_roi_delta") || "{}") };
  } catch {
    return { ...ROI_DELTAS_ZERO };
  }
};
